import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { customToast } from '@/components/ui/custom-toast';
import { useConfetti } from '@/hooks/useConfetti';
import { ChevronUp, ChevronDown, FlaskConical, Mail, Trash2, Search, Play, Square, Loader2, Clock, KeyRound } from 'lucide-react';
import { api, auth } from '@/services/api';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { ArticleService } from '@/services/ArticleService';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import type { CompanyEpisodeStats } from '@/types/episode.types';

const TEST_SERVER_URL = 'http://localhost:3099';

// Test scenario type from e2e/test-config.json
interface TestScenario {
  id: string;
  name: string;
  companyId: string;
  companyName: string;
  articleCount: number;
  publishMode: 'draft' | 'live' | 'scheduled';
}

export function DevToastTester() {
  // All hooks MUST be called before any conditional returns (React rules of hooks)
  const [isOpen, setIsOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [testEpisodeCount, setTestEpisodeCount] = useState<number | null>(null);
  const [testStatus, setTestStatus] = useState<'idle' | 'running' | 'passed' | 'failed'>('idle');
  const [testServerAvailable, setTestServerAvailable] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [scriptTestStatus, setScriptTestStatus] = useState<'idle' | 'running' | 'passed' | 'failed'>('idle');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [scriptTestProgress, setScriptTestProgress] = useState<{ currentClip: number; totalClips: number }[]>([{ currentClip: 0, totalClips: 10 }]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [scriptTestResults, setScriptTestResults] = useState<{ totalTime: number; perClipAvg: number }[] | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [parallelTestCount, setParallelTestCount] = useState<number>(1);
  const [workflowAccordionOpen, setWorkflowAccordionOpen] = useState(true);
  const [toastAccordionOpen, setToastAccordionOpen] = useState(false);
  // Test scenarios from config file
  const [testScenarios, setTestScenarios] = useState<TestScenario[]>([]);
  const [selectedScenarioIds, setSelectedScenarioIds] = useState<string[]>([]);
  // Queue progress for running multiple scenarios
  const [scenarioQueueProgress, setScenarioQueueProgress] = useState<{
    currentIndex: number;
    total: number;
    results: Array<{ id: string; status: 'pending' | 'running' | 'passed' | 'failed' }>;
  } | null>(null);
  // Legacy: Publish mode for E2E testing (kept for fallback)
  const [selectedPublishMode, setSelectedPublishMode] = useState<'all' | 'draft' | 'live' | 'scheduled'>('draft');
  // Legacy: Multi-company workflow test state (kept for fallback)
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [workflowTestProgress, setWorkflowTestProgress] = useState<{
    currentCompanyIndex: number;
    totalCompanies: number;
    currentCompanyId: string | null;
    results: Array<{
      companyId: string;
      companyName: string;
      status: 'pending' | 'running' | 'passed' | 'failed';
      error?: string;
    }>;
  } | null>(null);
  const pollInterval = useRef<NodeJS.Timeout | null>(null);
  const { fireCelebration } = useConfetti();
  const location = useLocation();
  const { userCompanies } = useUserCompanies();
  // Use companyStats from API to match Dashboard dropdown's data-company-id values
  const [companyStats, setCompanyStats] = useState<CompanyEpisodeStats[]>([]);
  const [tokenExpiration, setTokenExpiration] = useState<{ expiresAt: Date; isExpired: boolean; timeLeft: string } | null>(null);
  const [isCapturingAuth, setIsCapturingAuth] = useState(false);

  // Check isDev AFTER hooks - localhost only
  const isDev = process.env.NODE_ENV === 'development' ||
    (typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    ));

  // Check Firebase token expiration (for Playwright auth state)
  useEffect(() => {
    if (!isDev) return;

    const checkTokenExpiration = async () => {
      const user = auth.currentUser;
      if (!user) {
        setTokenExpiration(null);
        return;
      }

      try {
        const tokenResult = await user.getIdTokenResult();
        const expiresAt = new Date(tokenResult.expirationTime);
        const now = new Date();
        const isExpired = expiresAt <= now;
        const diffMs = expiresAt.getTime() - now.getTime();

        let timeLeft: string;
        if (isExpired) {
          timeLeft = 'Expired';
        } else if (diffMs < 60000) {
          timeLeft = `${Math.floor(diffMs / 1000)}s`;
        } else if (diffMs < 3600000) {
          timeLeft = `${Math.floor(diffMs / 60000)}m`;
        } else {
          timeLeft = `${Math.floor(diffMs / 3600000)}h ${Math.floor((diffMs % 3600000) / 60000)}m`;
        }

        setTokenExpiration({ expiresAt, isExpired, timeLeft });
      } catch (error) {
        console.error('Failed to check token expiration:', error);
        setTokenExpiration(null);
      }
    };

    checkTokenExpiration();
    // Check every 30 seconds
    const interval = setInterval(checkTokenExpiration, 30000);
    return () => clearInterval(interval);
  }, [isDev]);

  // Check if test server is available (only in dev)
  useEffect(() => {
    if (!isDev) return;
    const checkServer = async () => {
      try {
        const res = await fetch(`${TEST_SERVER_URL}/status`);
        if (res.ok) {
          setTestServerAvailable(true);
          const data = await res.json();
          setTestStatus(data.status);
        }
      } catch {
        setTestServerAvailable(false);
      }
    };
    checkServer();
    const interval = setInterval(checkServer, 5000);
    return () => clearInterval(interval);
  }, [isDev]);

  // Fetch test scenarios from config file
  useEffect(() => {
    if (!isDev) return;
    const loadScenarios = async () => {
      try {
        const res = await fetch(`${TEST_SERVER_URL}/test-scenarios`);
        if (res.ok) {
          const scenarios = await res.json();
          setTestScenarios(scenarios);
          // Select first scenario by default if none selected
          if (scenarios.length > 0 && selectedScenarioIds.length === 0) {
            setSelectedScenarioIds([scenarios[0].id]);
          }
        }
      } catch {
        console.log('Test scenarios not available - using legacy mode');
      }
    };
    loadScenarios();
  }, [isDev, selectedScenarioIds.length]);

  // Load companyStats from API - same source as Dashboard dropdown uses
  // This ensures we use the exact same companyId values as the dropdown's data-company-id
  useEffect(() => {
    if (!isDev) return;
    const loadCompanyStats = async () => {
      try {
        const stats = await PodcastEpisodeService.getMultiCompanyStats();
        setCompanyStats(stats);
      } catch (error) {
        console.error('Failed to load company stats for DevToastTester:', error);
      }
    };
    loadCompanyStats();
  }, [isDev]);

  // Initialize selectedCompanies with all companies when companyStats loads
  // Use companyStats.companyId (NOT userCompanies.id) to match Dashboard dropdown
  useEffect(() => {
    if (companyStats.length > 0 && selectedCompanies.length === 0) {
      setSelectedCompanies(companyStats.map(c => c.companyId));
    }
  }, [companyStats, selectedCompanies.length]);

  // Poll for test status while running (only in dev)
  useEffect(() => {
    if (!isDev) return;
    if (testStatus === 'running') {
      pollInterval.current = setInterval(async () => {
        try {
          const res = await fetch(`${TEST_SERVER_URL}/status`);
          if (res.ok) {
            const data = await res.json();
            setTestStatus(data.status);
            if (data.status === 'passed') {
              customToast.success('Test Passed!', { description: 'Workflow completed successfully' });
              fireCelebration();
              // Refresh page after test completes to show new episodes
              setTimeout(() => window.location.reload(), 2000);
            } else if (data.status === 'failed') {
              customToast.error('Test Failed', { description: 'Check console for details' });
              // Refresh page after test completes
              setTimeout(() => window.location.reload(), 2000);
            }
          }
        } catch {
          // Server unavailable
        }
      }, 2000);
    } else if (pollInterval.current) {
      clearInterval(pollInterval.current);
      pollInterval.current = null;
    }
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [isDev, testStatus, fireCelebration]);

  // Early return AFTER hooks - component won't render in production
  if (!isDev) return null;

  // Get episode ID from current URL
  const getEpisodeIdFromUrl = () => {
    const match = location.pathname.match(/episodes\/([a-zA-Z0-9]+)/);
    return match ? match[1] : null;
  };

  const currentEpisodeId = getEpisodeIdFromUrl();

  // Change episode status via local dev server (uses Firebase Admin SDK)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const changeEpisodeStatus = async (newStatus: string) => {
    const episodeId = currentEpisodeId;
    if (!episodeId) {
      customToast.error('No Episode', { description: 'Navigate to an episode detail page first' });
      return;
    }

    setIsChangingStatus(true);
    try {
      const res = await fetch(`${TEST_SERVER_URL}/change-episode-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ episodeId, status: newStatus })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || res.statusText);
      }

      customToast.success(`Status → ${newStatus}`);
    } catch (err: any) {
      if (err.message.includes('Failed to fetch')) {
        customToast.error('Dev Server Not Running', {
          description: 'Run: node scripts/dev-test-server.js'
        });
      } else {
        customToast.error('Failed', { description: err.message });
      }
    } finally {
      setIsChangingStatus(false);
    }
  };

  // Run the workflow test (legacy - for single company)
  const runWorkflowTest = async () => {
    try {
      const res = await fetch(`${TEST_SERVER_URL}/run-podcast-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ publishMode: selectedPublishMode })
      });
      if (res.ok) {
        setTestStatus('running');
        const modeLabel = selectedPublishMode === 'all' ? 'All Modes' : selectedPublishMode.charAt(0).toUpperCase() + selectedPublishMode.slice(1);
        customToast.info('Test Started', { description: `Running ${modeLabel} workflow test...` });
      } else {
        const data = await res.json();
        customToast.error('Failed to Start', { description: data.error || 'Unknown error' });
      }
    } catch {
      customToast.error('Test Server Unavailable', {
        description: 'Run: node scripts/dev-test-server.js',
      });
    }
  };

  // Run selected test scenarios sequentially
  const runSelectedScenarios = async () => {
    if (selectedScenarioIds.length === 0) {
      customToast.error('No Scenarios Selected', { description: 'Select at least one test scenario' });
      return;
    }

    const scenarios = selectedScenarioIds
      .map(id => testScenarios.find(s => s.id === id))
      .filter((s): s is TestScenario => s !== undefined);

    if (scenarios.length === 0) {
      customToast.error('Scenarios Not Found', { description: 'Selected scenarios no longer exist' });
      return;
    }

    // Initialize queue progress
    setScenarioQueueProgress({
      currentIndex: 0,
      total: scenarios.length,
      results: scenarios.map(s => ({ id: s.id, status: 'pending' as const })),
    });
    setTestStatus('running');

    // Run each scenario sequentially
    for (let i = 0; i < scenarios.length; i++) {
      const scenario = scenarios[i];

      // Update progress - mark current as running
      setScenarioQueueProgress(prev => {
        if (!prev) return prev;
        const newResults = [...prev.results];
        newResults[i] = { ...newResults[i], status: 'running' };
        return { ...prev, currentIndex: i, results: newResults };
      });

      customToast.info(`Running ${i + 1}/${scenarios.length}`, {
        description: scenario.name
      });

      try {
        const res = await fetch(`${TEST_SERVER_URL}/run-podcast-test`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyId: scenario.companyId,
            publishMode: scenario.publishMode,
            articleCount: scenario.articleCount,
          })
        });

        if (!res.ok) {
          throw new Error('Failed to start test');
        }

        // Poll for this test to complete
        const passed = await pollForTestCompletion();

        // Update result
        setScenarioQueueProgress(prev => {
          if (!prev) return prev;
          const newResults = [...prev.results];
          newResults[i] = { ...newResults[i], status: passed ? 'passed' : 'failed' };
          return { ...prev, results: newResults };
        });

      } catch (error: any) {
        // Mark as failed and continue
        setScenarioQueueProgress(prev => {
          if (!prev) return prev;
          const newResults = [...prev.results];
          newResults[i] = { ...newResults[i], status: 'failed' };
          return { ...prev, results: newResults };
        });
      }
    }

    // Calculate final results
    setScenarioQueueProgress(prev => {
      if (!prev) return prev;

      const passedCount = prev.results.filter(r => r.status === 'passed').length;
      const failedCount = prev.results.filter(r => r.status === 'failed').length;
      const allPassed = failedCount === 0;

      setTestStatus(allPassed ? 'passed' : 'failed');

      if (allPassed) {
        customToast.success('All Tests Passed!', {
          description: `${passedCount} scenario(s) completed successfully`
        });
        fireCelebration();
      } else {
        customToast.error('Some Tests Failed', {
          description: `Passed: ${passedCount}, Failed: ${failedCount}`
        });
      }

      return prev;
    });
  };

  // Helper to poll for test completion (returns true if passed, false if failed)
  const pollForTestCompletion = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const checkStatus = async () => {
        try {
          const res = await fetch(`${TEST_SERVER_URL}/status`);
          if (res.ok) {
            const data = await res.json();
            if (data.status === 'passed') {
              resolve(true);
              return;
            } else if (data.status === 'failed') {
              resolve(false);
              return;
            }
          }
        } catch {
          // Server unavailable - continue polling
        }

        // Still running, check again in 2 seconds
        setTimeout(checkStatus, 2000);
      };

      checkStatus();
    });
  };

  // Run multi-company workflow tests sequentially
  const runMultiCompanyWorkflowTest = async () => {
    // For single-company users, just run the simple test
    if (companyStats.length <= 1) {
      runWorkflowTest();
      return;
    }

    // Validate at least one company selected
    if (selectedCompanies.length === 0) {
      customToast.error('No Companies Selected', {
        description: 'Select at least one company to test'
      });
      return;
    }

    // Initialize progress tracking
    // Use companyStats to get company names (matches Dashboard dropdown data)
    const results = selectedCompanies.map(companyId => ({
      companyId,
      companyName: companyStats.find(c => c.companyId === companyId)?.companyName || 'Unknown',
      status: 'pending' as const,
    }));

    setWorkflowTestProgress({
      currentCompanyIndex: 0,
      totalCompanies: selectedCompanies.length,
      currentCompanyId: selectedCompanies[0],
      results,
    });

    setTestStatus('running');

    // Run tests sequentially for each company
    for (let i = 0; i < selectedCompanies.length; i++) {
      const companyId = selectedCompanies[i];
      const companyName = companyStats.find(c => c.companyId === companyId)?.companyName || 'Unknown';

      // Update progress - mark current as running
      setWorkflowTestProgress(prev => {
        if (!prev) return prev;
        const newResults = [...prev.results];
        newResults[i] = { ...newResults[i], status: 'running' };
        return { ...prev, currentCompanyIndex: i, currentCompanyId: companyId, results: newResults };
      });

      console.log(`🚀 DevToastTester: Starting test for company ID: "${companyId}" (${companyName})`);
      customToast.info(`Testing ${companyName}`, {
        description: `Company ${i + 1} of ${selectedCompanies.length} - ID: ${companyId}`
      });

      try {
        // Start test for this company
        const res = await fetch(`${TEST_SERVER_URL}/run-podcast-test`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ companyId, publishMode: selectedPublishMode })
        });
        console.log(`📤 DevToastTester: Sent request with companyId: "${companyId}"`);

        if (!res.ok) {
          throw new Error('Failed to start test');
        }

        // Poll for this test to complete
        const passed = await pollForTestCompletion();

        // Update result
        setWorkflowTestProgress(prev => {
          if (!prev) return prev;
          const newResults = [...prev.results];
          newResults[i] = { ...newResults[i], status: passed ? 'passed' : 'failed' };
          return { ...prev, results: newResults };
        });

      } catch (error: any) {
        // Mark as failed and continue with next company
        setWorkflowTestProgress(prev => {
          if (!prev) return prev;
          const newResults = [...prev.results];
          newResults[i] = {
            ...newResults[i],
            status: 'failed',
            error: error.message
          };
          return { ...prev, results: newResults };
        });
      }
    }

    // Calculate final results
    setWorkflowTestProgress(prev => {
      if (!prev) return prev;

      const passedCount = prev.results.filter(r => r.status === 'passed').length;
      const failedCount = prev.results.filter(r => r.status === 'failed').length;
      const allPassed = failedCount === 0;

      // Set final test status
      setTestStatus(allPassed ? 'passed' : 'failed');

      // Show summary toast
      if (allPassed) {
        customToast.success('All Tests Passed!', {
          description: `${passedCount} company test(s) completed successfully`
        });
        fireCelebration();
      } else {
        customToast.error('Some Tests Failed', {
          description: `Passed: ${passedCount}, Failed: ${failedCount}`
        });
      }

      return prev;
    });
  };

  // Stop the running test
  const stopTest = async () => {
    try {
      await fetch(`${TEST_SERVER_URL}/stop-test`, { method: 'POST' });
      setTestStatus('idle');
      customToast.info('Test Stopped');
    } catch {
      // Ignore
    }
  };

  // Find test episodes across ALL accessible companies + test scenario companies
  const findTestEpisodes = async () => {
    try {
      let totalCount = 0;
      const companyCounts: Record<string, number> = {};

      // Merge company IDs from companyStats AND test scenarios (for super admins)
      // This ensures we search all companies that might have test episodes
      const companyMap = new Map<string, string>(); // companyId -> companyName
      for (const company of companyStats) {
        companyMap.set(company.companyId, company.companyName);
      }
      for (const scenario of testScenarios) {
        if (!companyMap.has(scenario.companyId)) {
          companyMap.set(scenario.companyId, scenario.companyName);
        }
      }

      const allCompanies = Array.from(companyMap.entries()).map(([id, name]) => ({
        companyId: id,
        companyName: name,
      }));

      if (allCompanies.length > 0) {
        for (const company of allCompanies) {
          try {
            const episodes = await PodcastEpisodeService.getEpisodes({ companyIds: [company.companyId] });
            const testEpisodes = episodes.filter(ep => ep.title?.startsWith('[TEST]'));
            companyCounts[company.companyName] = testEpisodes.length;
            totalCount += testEpisodes.length;
          } catch {
            // Skip companies we don't have access to
            console.log(`Skipping company ${company.companyId} - no access`);
          }
        }
      } else {
        // Fallback: single company user with no test scenarios
        const episodes = await PodcastEpisodeService.getEpisodes();
        const testEpisodes = episodes.filter(ep => ep.title?.startsWith('[TEST]'));
        totalCount = testEpisodes.length;
      }

      setTestEpisodeCount(totalCount);

      if (totalCount === 0) {
        customToast.info('No Test Episodes', {
          description: allCompanies.length > 1
            ? `Searched ${allCompanies.length} company(ies)`
            : 'No episodes with [TEST] prefix found',
        });
      } else {
        // Build breakdown for multi-company users
        const breakdown = Object.entries(companyCounts)
          .filter(([, count]) => count > 0)
          .map(([name, count]) => `${name}: ${count}`)
          .join(', ');

        customToast.info(`Found ${totalCount} Test Episode(s)`, {
          description: allCompanies.length > 1 ? breakdown : 'Click "Delete Test Episodes" to remove them',
        });
      }
    } catch (error) {
      customToast.error('Search Failed', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  // Delete all test episodes from ALL accessible companies + test scenario companies
  const cleanupTestEpisodes = async () => {
    // Merge company IDs from companyStats AND test scenarios (for super admins)
    const companyIdSet = new Set<string>();
    for (const company of companyStats) {
      companyIdSet.add(company.companyId);
    }
    for (const scenario of testScenarios) {
      companyIdSet.add(scenario.companyId);
    }

    const allCompanyIds = Array.from(companyIdSet);

    if (allCompanyIds.length === 0) {
      customToast.error('No Companies', { description: 'No accessible companies found' });
      return;
    }

    setIsCleaningUp(true);

    try {
      // Try server endpoint first (faster, uses Admin SDK)
      const res = await fetch(`${TEST_SERVER_URL}/cleanup-test-episodes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyIds: allCompanyIds })
      });

      if (res.ok) {
        const data = await res.json();

        const totalDeleted = data.results.reduce((sum: number, r: { deleted?: number }) => sum + (r.deleted || 0), 0);
        const totalFailed = data.results.reduce((sum: number, r: { failed?: number }) => sum + (r.failed || 0), 0);

        setTestEpisodeCount(0);

        if (totalFailed === 0) {
          customToast.success(`Deleted ${totalDeleted} Episode(s)`, {
            description: allCompanyIds.length > 1
              ? `Cleaned up ${allCompanyIds.length} company(ies)`
              : 'All test episodes removed',
          });
        } else {
          customToast.warning(`Deleted ${totalDeleted}, Failed ${totalFailed}`, {
            description: 'Some episodes could not be deleted',
          });
        }
        setIsCleaningUp(false);
        return;
      }

      // Fall through to client-side cleanup if server returns error
      console.log('Server cleanup returned error, falling back to client-side');
    } catch {
      // Server unavailable - fall back to client-side cleanup
      console.log('Test server unavailable, using client-side cleanup');
    }

    // Client-side cleanup (slower, but works without test server)
    let totalDeleted = 0;
    let totalFailed = 0;

    for (const companyId of allCompanyIds) {
      try {
        // Get episodes for this company
        const episodes = await PodcastEpisodeService.getEpisodes({
          companyIds: [companyId]
        });

        const testEpisodes = episodes.filter(ep => ep.title?.startsWith('[TEST]'));

        for (const episode of testEpisodes) {
          try {
            if (episode.status !== 'archived') {
              await PodcastEpisodeService.archiveEpisode(episode.id);
            }
            await PodcastEpisodeService.deleteEpisode(episode.id);
            totalDeleted++;
          } catch {
            totalFailed++;
          }
        }
      } catch (error) {
        console.error(`Failed to clean company ${companyId}:`, error);
      }
    }

    setTestEpisodeCount(0);

    if (totalFailed === 0) {
      customToast.success(`Deleted ${totalDeleted} Episode(s)`, {
        description: allCompanyIds.length > 1
          ? `Cleaned up ${allCompanyIds.length} company(ies)`
          : 'All test episodes removed',
      });
    } else {
      customToast.warning(`Deleted ${totalDeleted}, Failed ${totalFailed}`, {
        description: 'Some episodes could not be deleted',
      });
    }

    setIsCleaningUp(false);
  };

  // Capture Playwright auth credentials
  const capturePlaywrightAuth = async () => {
    setIsCapturingAuth(true);
    try {
      const res = await fetch(`${TEST_SERVER_URL}/capture-auth`, { method: 'POST' });
      if (res.ok) {
        customToast.success('Auth Capture Started', {
          description: 'Complete login in the opened browser window',
        });
      } else {
        const data = await res.json();
        customToast.error('Failed to Start', { description: data.error || 'Unknown error' });
      }
    } catch {
      customToast.error('Test Server Unavailable', {
        description: 'Run: node scripts/dev-test-server.js',
      });
    } finally {
      setIsCapturingAuth(false);
    }
  };

  const sendTestEmail = async () => {
    setIsSendingEmail(true);
    try {
      const response = await api.post<{ success: boolean; error?: { message: string } }>('/email/test', {
        to: 'sean@evansconsultingservices.com',
        subject: 'Test Email from Media Orchestrator',
        message: 'This is a test email to verify that AWS SES is configured correctly.',
      });

      if (response.data?.success) {
        customToast.success('Test Email Sent!', {
          description: `Email sent to sean@evansconsultingservices.com`,
        });
      } else {
        customToast.error('Email Failed', {
          description: response.data?.error?.message || 'Unknown error',
        });
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send email';
      customToast.error('Email Failed', {
        description: errorMessage,
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Run a single script generation test - returns result
  const runSingleScriptGenTest = async (
    testIndex: number,
    updateProgress: (index: number, progress: { currentClip: number; totalClips: number }) => void
  ): Promise<{ totalTime: number; perClipAvg: number }> => {
    // 1. Create test episode (draft with default title)
    const episode = await PodcastEpisodeService.createDraftEpisode();

    // 2. Update title/description via inline editor pattern
    const testTitle = `[TEST] Script Gen Speed Test #${testIndex + 1} - ${new Date().toISOString()}`;
    await PodcastEpisodeService.updateEpisode(episode.id, {
      title: testTitle,
      description: `Automated test #${testIndex + 1} for measuring script generation performance`,
    });
    customToast.info(`Test #${testIndex + 1}: Episode Created`, { description: episode.id.slice(0, 8) });

    // 3. Fetch articles
    const { articles } = await ArticleService.fetchArticles();
    if (articles.length < 10) {
      throw new Error(`Not enough articles available (${articles.length}/10)`);
    }

    // 4. Add 10 clips
    const articlesForClip = articles.slice(0, 10).map(article => ({
      id: article.id,
      title: article.title,
      url: article.articleUrl,
      content: article.content,
      excerpt: article.excerpt,
      author: article.author,
      publishedAt: article.publishDate?.toISOString(),
      imageUrl: article.imageUrl,
      feedLabel: typeof article.sourcePlatform === 'string' ? article.sourcePlatform : undefined,
    }));
    const testCompanyId = userCompanies[0]?.id;
    if (!testCompanyId) {
      throw new Error('No company available for test');
    }
    await PodcastEpisodeService.addClipsToEpisode(episode.id, articlesForClip, testCompanyId);
    customToast.info(`Test #${testIndex + 1}: Clips Added`, { description: '10 clips ready' });

    // 5. Start timing and trigger script generation
    const startTime = performance.now();
    await PodcastEpisodeService.generateScripts(episode.id);
    customToast.info(`Test #${testIndex + 1}: Script Generation Started`, { description: 'Timing in progress...' });

    // 6. Poll until status is SCRIPTS_PENDING_REVIEW
    let scriptsComplete = false;
    while (!scriptsComplete) {
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Fetch episode and clips separately
      const [updatedEpisode, clips] = await Promise.all([
        PodcastEpisodeService.getEpisode(episode.id),
        PodcastEpisodeService.getClips(episode.id),
      ]);

      if (!updatedEpisode) {
        throw new Error('Failed to fetch episode status');
      }

      // Count completed scripts
      const completedCount = clips?.filter(c => c.script).length || 0;
      updateProgress(testIndex, { currentClip: completedCount, totalClips: 10 });

      if (updatedEpisode.status === 'scripts_pending_review') {
        scriptsComplete = true;
      } else if (updatedEpisode.status === 'failed') {
        throw new Error(`Test #${testIndex + 1}: Script generation failed`);
      }
    }

    // 7. Calculate timing
    const endTime = performance.now();
    const totalSeconds = (endTime - startTime) / 1000;

    return {
      totalTime: Math.round(totalSeconds * 10) / 10,
      perClipAvg: Math.round((totalSeconds / 10) * 10) / 10,
    };
  };

  // Run script generation test with 10 clips (supports parallel tests)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const runScriptGenTest = async () => {
    let originalMaxArticles: number | undefined;

    try {
      setScriptTestStatus('running');
      // Initialize progress for all tests
      setScriptTestProgress(Array(parallelTestCount).fill(null).map(() => ({ currentClip: 0, totalClips: 10 })));
      setScriptTestResults(null);

      // 1. Get current settings and save original value
      const settings = await PodcastEpisodeService.getPodcastSettings();
      originalMaxArticles = settings.maxArticlesPerEpisode || 5;

      // 2. Update to 10 if needed
      if (originalMaxArticles !== 10) {
        await PodcastEpisodeService.updatePodcastSettings({ maxArticlesPerEpisode: 10 });
        customToast.info('Settings Updated', { description: 'maxArticlesPerEpisode set to 10' });
      }

      // Progress update callback
      const updateProgress = (index: number, progress: { currentClip: number; totalClips: number }) => {
        setScriptTestProgress(prev => {
          const newProgress = [...prev];
          newProgress[index] = progress;
          return newProgress;
        });
      };

      // 3. Run tests in parallel
      const testPromises = Array(parallelTestCount).fill(null).map((_, index) =>
        runSingleScriptGenTest(index, updateProgress)
      );

      const results = await Promise.all(testPromises);

      // 4. Set results and show success
      setScriptTestResults(results);
      setScriptTestStatus('passed');
      fireCelebration();

      const avgTotal = results.reduce((sum, r) => sum + r.totalTime, 0) / results.length;
      customToast.success(`${parallelTestCount} Test(s) Complete`, {
        description: `Avg Total: ${avgTotal.toFixed(1)}s`,
      });

    } catch (error) {
      setScriptTestStatus('failed');
      customToast.error('Script Gen Test Failed', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      // 5. Restore original settings
      if (originalMaxArticles !== undefined && originalMaxArticles !== 10) {
        try {
          await PodcastEpisodeService.updatePodcastSettings({
            maxArticlesPerEpisode: originalMaxArticles
          });
          customToast.info('Settings Restored', { description: `maxArticlesPerEpisode reset to ${originalMaxArticles}` });
        } catch (e) {
          console.error('Failed to restore settings:', e);
        }
      }
    }
  };

  return (
    <div
      className="fixed z-50"
      style={{ fontFamily: 'system-ui, sans-serif', padding: '10', left: '5px', bottom: isOpen ? '140px' : '8px', maxWidth: '240px' }}
    >
      {isOpen ? (
        <div
          className="rounded-lg shadow-xl"
          style={{
            width: '240px',
            backgroundColor: '#0d1214',
            border: '1px solid #2d3a3d',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-3 py-2 cursor-pointer"
            style={{ borderBottom: '1px solid #2d3a3d' }}
            onClick={() => setIsOpen(false)}
          >
            <div className="flex items-center gap-4">
              <FlaskConical className="h-4 w-4" style={{ color: '#22D3EE' }} />
              <span className="text-sm font-medium" style={{ color: '#f0f0f0' }}>
                Dev Tools
              </span>
            </div>
            <ChevronDown className="h-5 w-5" style={{ color: '#8a9a9e' }} />
          </div>

          {/* Auth Token Status */}
          {tokenExpiration && (
            <div
              className="flex items-center justify-between px-3 py-1.5 text-xs"
              style={{
                backgroundColor: tokenExpiration.isExpired ? 'rgba(239, 68, 68, 0.1)' : 'rgba(34, 197, 94, 0.1)',
                borderBottom: '1px solid #2d3a3d',
              }}
            >
              <div className="flex items-center gap-1.5">
                <Clock className="h-3 w-3" style={{ color: tokenExpiration.isExpired ? '#ef4444' : '#22c55e' }} />
                <span style={{ color: '#8a9a9e' }}>Auth Token:</span>
              </div>
              <span style={{ color: tokenExpiration.isExpired ? '#ef4444' : '#22c55e', fontWeight: 500 }}>
                {tokenExpiration.isExpired ? 'EXPIRED' : tokenExpiration.timeLeft}
              </span>
            </div>
          )}

          {/* Content */}
          <div className="p-4 space-y-1">
            {/* Workflow Testing Accordion */}
            <div style={{ border: '1px solid #2d3a3d', borderRadius: '6px', overflow: 'hidden' }}>
              <button
                className="w-full flex items-center justify-between px-3 py-2 text-left"
                style={{ background: workflowAccordionOpen ? 'rgba(0, 212, 255, 0.05)' : 'transparent' }}
                onClick={() => setWorkflowAccordionOpen(!workflowAccordionOpen)}
              >
                <span className="text-xs font-medium" style={{ color: '#f0f0f0' }}>
                  Workflow Testing
                </span>
                {workflowAccordionOpen ? (
                  <ChevronDown className="h-5 w-5" style={{ color: '#8a9a9e' }} />
                ) : (
                  <ChevronUp className="h-5 w-5" style={{ color: '#8a9a9e' }} />
                )}
              </button>
              {workflowAccordionOpen && (
                <div className="px-3 pb-3 space-y-3">
                  {/* E2E Testing Section */}
                  <div style={{ borderBottom: '1px solid #2d3a3d', paddingBottom: '12px' }}>
                    <div className="text-xs font-medium mb-2" style={{ color: '#8a9a9e' }}>
                      E2E TESTING
                    </div>

                    {/* Test Scenario Selector with Multi-Select */}
                    {testScenarios.length > 0 ? (
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs mb-2">
                          <span style={{ color: '#6b7a7d' }}>Test Scenarios:</span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setSelectedScenarioIds(testScenarios.map(s => s.id))}
                              disabled={testStatus === 'running'}
                              className="text-xs hover:underline"
                              style={{ color: '#3ecfb2' }}
                            >
                              All
                            </button>
                            <button
                              onClick={() => setSelectedScenarioIds([])}
                              disabled={testStatus === 'running'}
                              className="text-xs hover:underline"
                              style={{ color: '#8a9a9e' }}
                            >
                              None
                            </button>
                          </div>
                        </div>
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                          {testScenarios.map((scenario) => {
                            const isSelected = selectedScenarioIds.includes(scenario.id);
                            const queueResult = scenarioQueueProgress?.results.find(r => r.id === scenario.id);
                            return (
                              <button
                                key={scenario.id}
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedScenarioIds(selectedScenarioIds.filter(id => id !== scenario.id));
                                  } else {
                                    setSelectedScenarioIds([...selectedScenarioIds, scenario.id]);
                                  }
                                }}
                                disabled={testStatus === 'running'}
                                className="w-full flex items-start gap-2 px-2 py-2 text-xs rounded transition-colors text-left"
                                style={{
                                  background: isSelected ? '#3ecfb2' : '#2d3a3d',
                                  color: isSelected ? '#000' : '#f0f0f0',
                                  opacity: testStatus === 'running' ? 0.7 : 1,
                                  minHeight: '60px',
                                }}
                              >
                                {/* Checkbox indicator */}
                                <span
                                  className="flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center mt-0.5"
                                  style={{
                                    borderColor: isSelected ? '#000' : '#6b7a7d',
                                    background: isSelected ? '#000' : 'transparent',
                                  }}
                                >
                                  {isSelected && <span style={{ color: '#3ecfb2', fontSize: '10px' }}>✓</span>}
                                </span>
                                <span className="flex-1 line-clamp-3" style={{ wordBreak: 'break-word' }}>{scenario.name}</span>
                                {/* Status indicator when running */}
                                {queueResult && testStatus === 'running' && (
                                  <span
                                    className="flex-shrink-0"
                                    style={{
                                      color: queueResult.status === 'passed' ? '#22c55e' :
                                             queueResult.status === 'failed' ? '#ef4444' :
                                             queueResult.status === 'running' ? '#fbbf24' : '#8a9a9e',
                                    }}
                                  >
                                    {queueResult.status === 'passed' ? '✓' :
                                     queueResult.status === 'failed' ? '✗' :
                                     queueResult.status === 'running' ? '...' : '○'}
                                  </span>
                                )}
                                {/* Article count badge */}
                                {!queueResult && (
                                  <span
                                    className="text-xs px-1 rounded flex-shrink-0"
                                    style={{
                                      background: isSelected ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.1)',
                                      color: isSelected ? '#000' : '#8a9a9e',
                                    }}
                                  >
                                    {scenario.articleCount}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      /* Fallback: Legacy publish mode selector if no scenarios */
                      <div className="mb-3">
                        <div className="text-xs mb-2" style={{ color: '#6b7a7d' }}>
                          Publish Mode:
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          {(['draft', 'live', 'scheduled', 'all'] as const).map(mode => (
                            <button
                              key={mode}
                              onClick={() => setSelectedPublishMode(mode)}
                              disabled={testStatus === 'running'}
                              className="px-2 py-1 text-xs rounded transition-colors"
                              style={{
                                background: selectedPublishMode === mode ? '#3ecfb2' : '#2d3a3d',
                                color: selectedPublishMode === mode ? '#000' : '#8a9a9e',
                                opacity: testStatus === 'running' ? 0.5 : 1,
                              }}
                            >
                              {mode === 'all' ? 'All' : mode.charAt(0).toUpperCase() + mode.slice(1)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Progress display when running */}
                    {scenarioQueueProgress && testStatus === 'running' && (
                      <div className="mb-3 p-2 rounded text-xs" style={{ background: '#1c2128' }}>
                        <div className="flex items-center justify-between mb-1">
                          <span style={{ color: '#8a9a9e' }}>Progress:</span>
                          <span style={{ color: '#3ecfb2' }}>
                            {scenarioQueueProgress.currentIndex + 1}/{scenarioQueueProgress.total}
                          </span>
                        </div>
                        <div className="h-1.5 rounded-full" style={{ background: '#2d3a3d' }}>
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${((scenarioQueueProgress.currentIndex + 1) / scenarioQueueProgress.total) * 100}%`,
                              background: '#3ecfb2',
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {testStatus === 'running' ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="w-full"
                        onClick={stopTest}
                      >
                        <Square className="h-3 w-3 mr-1" />
                        Stop Test
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant={testServerAvailable ? 'default' : 'secondary'}
                        className="w-full"
                        onClick={testScenarios.length > 0 ? runSelectedScenarios : runMultiCompanyWorkflowTest}
                        disabled={!testServerAvailable || (testScenarios.length > 0 && selectedScenarioIds.length === 0)}
                      >
                        {testServerAvailable ? (
                          <>
                            <Play className="h-3 w-3 mr-1" />
                            {testScenarios.length > 0
                              ? `Run Selected (${selectedScenarioIds.length})`
                              : `Run ${selectedPublishMode === 'all' ? 'All' : selectedPublishMode.charAt(0).toUpperCase() + selectedPublishMode.slice(1)}`
                            }
                          </>
                        ) : (
                          <>
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Start test server first
                          </>
                        )}
                      </Button>
                    )}

                    {testStatus !== 'idle' && testStatus !== 'running' && (
                      <div className="text-xs mt-1 text-center" style={{ color: testStatus === 'passed' ? '#22c55e' : '#ef4444' }}>
                        Last run: {testStatus === 'passed' ? '✓ Passed' : '✗ Failed'}
                      </div>
                    )}

                    {/* Auth Capture Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full mt-2"
                      onClick={capturePlaywrightAuth}
                      disabled={!testServerAvailable || isCapturingAuth}
                      style={{
                        borderColor: tokenExpiration?.isExpired ? '#ef4444' : undefined,
                        color: tokenExpiration?.isExpired ? '#ef4444' : undefined,
                      }}
                    >
                      <KeyRound className="h-3 w-3 mr-1" />
                      {isCapturingAuth ? 'Opening...' : tokenExpiration?.isExpired ? 'Refresh Auth (Expired)' : 'Capture Auth'}
                    </Button>
                  </div>

                  {/* Test Episode Cleanup Section */}
                  <div>
                    <div className="text-xs font-medium mb-2" style={{ color: '#8a9a9e' }}>
                      TEST CLEANUP
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={findTestEpisodes}
                      >
                        <Search className="h-3 w-3 mr-1" />
                        Find
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={cleanupTestEpisodes}
                        disabled={isCleaningUp}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        {isCleaningUp ? 'Deleting...' : testEpisodeCount !== null ? `Delete (${testEpisodeCount})` : 'Delete'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Toast Testing Accordion */}
            <div style={{ border: '1px solid #2d3a3d', borderRadius: '6px', overflow: 'hidden' }}>
              <button
                className="w-full flex items-center justify-between px-3 py-2 text-left"
                style={{ background: toastAccordionOpen ? 'rgba(0, 212, 255, 0.05)' : 'transparent' }}
                onClick={() => setToastAccordionOpen(!toastAccordionOpen)}
              >
                <span className="text-xs font-medium" style={{ color: '#f0f0f0' }}>
                  Toast Testing
                </span>
                {toastAccordionOpen ? (
                  <ChevronDown className="h-3 w-3" style={{ color: '#8a9a9e' }} />
                ) : (
                  <ChevronUp className="h-3 w-3" style={{ color: '#8a9a9e' }} />
                )}
              </button>
              {toastAccordionOpen && (
                <div className="px-3 pb-3 space-y-2">
                  <Button
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      customToast.celebration('Episode Published!', {
                        description: '"Weekly Tech Roundup #47" is now live on PodBean',
                        onClick: () => console.log('Navigate to episode'),
                        withConfetti: true,
                      });
                    }}
                  >
                    Publish Celebration
                  </Button>

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        customToast.success('Script Approved', {
                          description: 'Moving to audio generation',
                        });
                      }}
                    >
                      Success
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        customToast.error('Generation Failed', {
                          description: 'Please try again',
                        });
                      }}
                    >
                      Error
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        customToast.info('Audio Generation Complete', {
                          description: 'Ready for review',
                        });
                      }}
                    >
                      Info
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        customToast.warning('Processing', {
                          description: 'This may take a moment',
                        });
                      }}
                    >
                      Warning
                    </Button>
                  </div>

                  <Button
                    size="sm"
                    variant="secondary"
                    className="w-full"
                    onClick={() => {
                      customToast.success('First', { description: 'Stack test 1/3' });
                      setTimeout(() => customToast.info('Second', { description: 'Stack test 2/3' }), 200);
                      setTimeout(() => customToast.warning('Third', { description: 'Stack test 3/3' }), 400);
                    }}
                  >
                    Stack Test (3 toasts)
                  </Button>

                  <Button
                    size="sm"
                    variant="secondary"
                    className="w-full"
                    onClick={() => fireCelebration()}
                  >
                    Confetti Only
                  </Button>

                  <Button
                    size="sm"
                    variant="secondary"
                    className="w-full"
                    onClick={sendTestEmail}
                    disabled={isSendingEmail}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    {isSendingEmail ? 'Sending...' : 'Send Test Email'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-1.5 px-2 rounded-lg shadow-lg transition-colors"
          style={{
            height: '36px',
            backgroundColor: '#0d1214',
            border: `1px solid ${tokenExpiration?.isExpired ? '#ef4444' : '#2d3a3d'}`,
            color: '#f0f0f0',
          }}
        >
          <FlaskConical className="h-4 w-4" style={{ color: '#22D3EE' }} />
          {tokenExpiration && (
            <span
              className="text-xs px-1 py-0.5 rounded"
              style={{
                backgroundColor: tokenExpiration.isExpired ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                color: tokenExpiration.isExpired ? '#ef4444' : '#22c55e',
              }}
            >
              {tokenExpiration.isExpired ? '!' : tokenExpiration.timeLeft}
            </span>
          )}
          <ChevronUp className="h-3 w-3" style={{ color: '#8a9a9e' }} />
        </button>
      )}
    </div>
  );
}
